// AstriaIndonesiaV2Shell.tsx
import React from "react";
import { useLaneCopy } from "../../../utils/laneCopy";
import { useNavigate } from "react-router-dom";
import { Moon, Sun, Hourglass, CloudSun, Compass, NotebookPen } from "lucide-react";
import { IndonesiaV2Lang } from "./indonesiaV2Language";
import { INDONESIA_V2_COPY } from "./indonesiaV2Copy";

// ─── Astria Indonesia v2 — shared shell (header + sticky tab bar) ─────────
// Per client spec (Vn.txt — ID v2 Full Deployment JSON Pack): warm-friendly,
// simple, soft-neutral, reflective-light. An amber / warm-teal / cream
// palette — distinct from Philippines v2's coral/rose-gold, same
// structural pattern (see AstriaPhilippinesV2Shell.tsx / AstriaIndiaV2Shell.tsx).
// 6 fixed tabs, no birth chart, no LLM — see
// helper/astriaIndonesiaV2Service.js on the backend.
//
// Bilingual (EN/ID) — UI chrome text comes from indonesiaV2Copy.ts in the user's
// preferred language (see indonesiaV2Language.ts); there is no per-screen toggle.

export const INDONESIA_V2_HEADING_FONT = "'Poppins', 'Inter', sans-serif";
export const INDONESIA_V2_BODY_FONT = "'Inter', sans-serif";
// Shared cap for header/tab-bar/body so the panel fills more of the chat
// column on wide screens instead of floating with large side margins.
const INDONESIA_V2_MAX_WIDTH = 960;

export const SHELL_COLORS_INDONESIA_V2 = {
  bg: "linear-gradient(160deg, #FFFDF7 0%, #FFF6E4 55%, #F3FBF7 100%)",
  card: "#FFFFFF",
  section: "#FFF4DE",
  cardBorder: "#F3DFAE",
  titleColor: "#3E351A",
  sectionTitleColor: "#3E351A",
  bodyColor: "#5A4E28",
  mutedColor: "#A6935F",
  accent: "#D6A335",
  accentSoft: "rgba(214,163,53,0.14)",
  accentGlow: "0 0 0 3px rgba(214,163,53,0.18)",
  gold: "#2E9E8B",
  goldSoft: "rgba(46,158,139,0.14)",
  rose: "#F2C879",
  white: "#FFFFFF",
};

export type AstriaIndonesiaV2Tab =
  | "soft_summary"
  | "connection_atmosphere"
  | "timing_rhythm"
  | "emotion_balance"
  | "guidance_path"
  | "your_note";

interface TabDef {
  key: AstriaIndonesiaV2Tab;
  // The label is derived from the copy file, so it is not hardcoded here.
  dbName: string;
  icon: React.ReactNode;
  path: string;
  accent: [string, string];
}

// Icons/order per Vn.txt's ID v2 UI pack: 🌙 💛 ⏳ 🌤️ 🧭 📝
const TABS: TabDef[] = [
  {
    key: "soft_summary",
    dbName: "Soft Summary",
    icon: <Moon className="w-4 h-4" />,
    path: "/astria-indonesia-v2/soft-summary",
    accent: ["#D6A335", "#F2C879"],
  },
  {
    key: "connection_atmosphere",
    dbName: "Connection Atmosphere",
    icon: <Sun className="w-4 h-4" />,
    path: "/astria-indonesia-v2/connection-atmosphere",
    accent: ["#F2C879", "#2E9E8B"],
  },
  {
    key: "timing_rhythm",
    dbName: "Timing Rhythm",
    icon: <Hourglass className="w-4 h-4" />,
    path: "/astria-indonesia-v2/timing-rhythm",
    accent: ["#2E9E8B", "#D6A335"],
  },
  {
    key: "emotion_balance",
    dbName: "Emotion Balance",
    icon: <CloudSun className="w-4 h-4" />,
    path: "/astria-indonesia-v2/emotion-balance",
    accent: ["#F2C879", "#D6A335"],
  },
  {
    key: "guidance_path",
    dbName: "Guidance Path",
    icon: <Compass className="w-4 h-4" />,
    path: "/astria-indonesia-v2/guidance-path",
    accent: ["#D6A335", "#2E9E8B"],
  },
  {
    key: "your_note",
    dbName: "Your Note",
    icon: <NotebookPen className="w-4 h-4" />,
    path: "/astria-indonesia-v2/your-note",
    accent: ["#2E9E8B", "#F2C879"],
  },
];

interface AstriaIndonesiaV2ShellProps {
  active: AstriaIndonesiaV2Tab;
  lang: IndonesiaV2Lang;
  children: React.ReactNode;
}

const AstriaIndonesiaV2Shell: React.FC<AstriaIndonesiaV2ShellProps> = ({
  active,
  lang,
  children,
}) => {
  const navigate = useNavigate();
  const copy = useLaneCopy("v2", INDONESIA_V2_COPY, lang);

  return (
    <div
      className="min-h-screen"
      style={{
        background: SHELL_COLORS_INDONESIA_V2.bg,
        fontFamily: INDONESIA_V2_BODY_FONT,
      }}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          background: "rgba(255,253,247,0.82)",
          borderBottom: `1px solid ${SHELL_COLORS_INDONESIA_V2.cardBorder}`,
        }}
      >
        <div
          className="mx-auto flex items-center gap-3 px-5"
          style={{ maxWidth: INDONESIA_V2_MAX_WIDTH, paddingTop: 18, paddingBottom: 14 }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${SHELL_COLORS_INDONESIA_V2.accent} 0%, ${SHELL_COLORS_INDONESIA_V2.gold} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 4px 14px ${SHELL_COLORS_INDONESIA_V2.accentSoft}`,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: 16, color: "#fff" }}>💛</span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: SHELL_COLORS_INDONESIA_V2.titleColor,
                fontFamily: INDONESIA_V2_HEADING_FONT,
                letterSpacing: "0.01em",
              }}
            >
              {copy.shell.title}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: SHELL_COLORS_INDONESIA_V2.mutedColor,
              }}
            >
              {copy.shell.subtitle}
            </div>
          </div>

        </div>

        {/* ─── Tab bar ───────────────────────────────────────────────── */}
        <div style={{ position: "relative" }}>
          <div
            className="mx-auto px-5 pb-3 hide-scrollbar"
            style={{
              maxWidth: INDONESIA_V2_MAX_WIDTH,
              display: "flex",
              gap: 8,
              overflowX: "auto",
              scrollbarWidth: "none",
            }}
          >
            {TABS.map((tab) => {
              const isActive = tab.key === active;
              return (
                <button
                  key={tab.key}
                  onClick={() => navigate(tab.path)}
                  className="flex items-center gap-1.5 rounded-full transition-all whitespace-nowrap"
                  style={{
                    paddingInline: 14,
                    paddingBlock: 8,
                    fontSize: 12.5,
                    fontWeight: 600,
                    flexShrink: 0,
                    color: isActive
                      ? "#FFFFFF"
                      : SHELL_COLORS_INDONESIA_V2.bodyColor,
                    background: isActive
                      ? `linear-gradient(135deg, ${tab.accent[0]} 0%, ${tab.accent[1]} 100%)`
                      : SHELL_COLORS_INDONESIA_V2.section,
                    boxShadow: isActive
                      ? `0 4px 14px ${SHELL_COLORS_INDONESIA_V2.accentSoft}`
                      : "none",
                    border: isActive
                      ? "none"
                      : `1px solid ${SHELL_COLORS_INDONESIA_V2.cardBorder}`,
                  }}
                >
                  {tab.icon}
                  <span>{copy.tabBar[tab.key].label}</span>
                </button>
              );
            })}
          </div>
          {/* Right-edge fade so a clipped last tab reads as "scroll for
              more" rather than a layout bug — the row has no scrollbar
              (hidden above) so this is the only affordance. */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 12,
              width: 28,
              pointerEvents: "none",
              background:
                "linear-gradient(90deg, rgba(255,253,247,0) 0%, rgba(255,253,247,0.9) 100%)",
            }}
          />
        </div>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <div
        className="mx-auto px-4 sm:px-5"
        style={{ maxWidth: INDONESIA_V2_MAX_WIDTH, paddingTop: 24, paddingBottom: 64 }}
      >
        {children}
      </div>
    </div>
  );
};

export default AstriaIndonesiaV2Shell;
export { TABS as ASTRIA_INDONESIA_V2_TABS };
